import type { CSSProperties } from 'react'
import {
  InfoIcon,
  PANEL,
  PointIcon,
  RewardTile,
  StatGrid,
  StatusPill,
  StickerIcon,
  TrendUpIcon,
} from '../../../shared/components'
import { PARENT_COPY, type Lang } from './copy'
import { stickerBook, type StickerGroup } from './stickerBook'

/**
 * Coins, stickers and the day count — the three things a child talks about.
 *
 * 🔴 THE SHELF IS WHY THIS MODULE EXISTS. C3 used to render the raw sticker ids
 * — `sky-quest-l2`, `letter-lagoon-l1` — inside `StatusPill`s, so the one screen
 * in the product about a child's reward printed database slugs at their family.
 * `gameArt.ts` records the identical complaint one tier up: *"E5 was printing
 * database slugs at a head teacher"*. `stickerBook` turns an id back into a game
 * and a level, and the shelf shows the plate the child actually taps.
 *
 * ✅ THE THREE FIGURES ABOVE IT ARE THE KIT'S `RewardTile` NOW. They spent a
 * release as full-width cards carrying a paragraph, a worked sum and a note
 * each — the explanation the counts had always lacked, delivered in the one
 * place it crowded out the count. The teacher's Leaderboard draws these same
 * three as a label, a numeral and a caption; a parent and their teacher now see
 * one design for one fact.
 *
 * ⚠️ THE EXPLANATION IS STILL TRUE AND STILL WORTH SAYING SOMEWHERE. `rewards.ts`
 * is `coins: correct * level`, a sticker is a four-in-five round, and coins buy
 * nothing because there is no shop — the question a family opens this screen
 * with. `stickerBook.coinsForRound` still holds that arithmetic and
 * `stickerBook.test.ts` still pins it to `calculateReward`, so the day the game's
 * sums change a test fails. What it no longer has is a home on screen: if it
 * returns it belongs in a section under the tiles, not inside them.
 */

export interface RewardsPanelProps {
  lang: Lang
  coins: number
  stickers: readonly string[]
  streakDays: number
}

/**
 * The section itself — three tiles, then the shelf.
 *
 * ⚠️ IT TAKES THE STICKER **IDS**, NOT A COUNT, and that is what lets the shelf
 * exist at all. C1's dashboard payload carries `stickers: number`; the ids come
 * from `GET /progress/:playerId`, which C3 already reads. The caller fetches;
 * this draws.
 */
export function RewardsPanel({ lang, coins, stickers, streakDays }: RewardsPanelProps) {
  const t = PARENT_COPY[lang]
  const groups = stickerBook(stickers)

  return (
    <>
      {/*
        🔴 THE KIT'S TILE, THE SAME ONE THE TEACHER'S LEADERBOARD DRAWS. This
        section had its own `RewardTile` — a full-width card carrying a
        paragraph, a worked sum and a note under each numeral — so the three
        figures a family talks about were drawn one way for a parent and another
        way for their teacher, and the parent's version was ninety words of prose
        in three boxes. `DashUI`'s tile was written to end exactly that split
        one tier up: *"same numbers, same words, two designs"*.

        ⚠️ SO THE EXPLANATIONS ARE GONE FROM THE TILES RATHER THAN SHRUNK INSIDE
        THEM. A caption is four words; there is no honest way to fit "coins buy
        nothing, there is no shop" into one. If that answer comes back it belongs
        under this grid as a section of its own, where it can be read rather than
        skimmed past on the way to the number.
      */}
      <StatGrid>
        <RewardTile
          label={t.coinsWhat}
          value={coins}
          caption={t.coinsCaption}
          icon={<PointIcon size={18} />}
          tone="sunshine"
        />
        <RewardTile
          label={t.stickersWhat}
          value={stickers.length}
          caption={t.stickersCaption}
          icon={<StickerIcon size={18} />}
          tone="primary"
        />
        <RewardTile
          label={t.streakWhat}
          value={streakDays}
          caption={t.streakCaption}
          icon={<TrendUpIcon size={18} />}
          tone="leaf"
        />
      </StatGrid>

      <div className="mt-4">
        <StickerShelf groups={groups} lang={lang} />
      </div>
    </>
  )
}

/* ───────────────────────────────────────────────────────── the sticker shelf ── */

/**
 * The stickers, as the things they were earned for.
 *
 * ✅ THIS REPLACED A ROW OF PILLS PRINTING RAW IDS. `StatusPill` with
 * `sky-quest-l2` inside it was honest about the data and useless to a family;
 * `stickerBook` turns the id back into a game and a level, so the shelf reads
 * *"Sky Quest · Level 2"* beside the plate the child actually taps.
 *
 * ⚠️ A STICKER IS STILL NOT A CONTROL. It opens nothing, and making it a button
 * would promise a screen that does not exist — `PlayfulUI`'s original note, kept.
 * The wobble on hover is the one place `--ease-kid` is allowed in this tier: the
 * child's reward being picked up and put back.
 */
export function StickerShelf({ groups, lang }: { groups: readonly StickerGroup[]; lang: Lang }) {
  const t = PARENT_COPY[lang]

  if (groups.length === 0) {
    return (
      <div className={`${PANEL} p-5`}>
        <p className="text-dash-sm text-muted">{t.stickerShelfEmpty}</p>
      </div>
    )
  }

  return (
    <div className={`${PANEL} p-5`}>
      <p className="mb-4 font-dash text-dash-sm font-bold text-ink">{t.stickerShelf}</p>
      <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {groups.map((group, i) => (
          <li
            key={group.game?.id ?? 'unknown'}
            className="play-rise flex items-center gap-3 rounded-lg border border-line p-3"
            style={{ '--rise-i': i } as CSSProperties}
          >
            {group.game ? (
              /* The plate the child taps, cropped square so a row of them reads
                 as a shelf rather than as five different pictures. */
              <img
                src={group.game.art}
                alt=""
                aria-hidden="true"
                className="h-14 w-14 shrink-0 rounded-md object-cover select-none"
                loading="lazy"
                draggable={false}
              />
            ) : (
              <span
                aria-hidden="true"
                className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md border border-line text-muted"
              >
                <InfoIcon size={20} />
              </span>
            )}

            <span className="flex min-w-0 flex-col gap-1.5">
              <span className="truncate font-dash text-dash-sm font-bold text-ink">
                {group.game ? group.game.title[lang] : t.stickerUnknown}
              </span>
              <span className="flex flex-wrap gap-1.5">
                {group.stickers.map((sticker) => (
                  <span key={sticker.id} className="play-pop">
                    <StatusPill tone="success" icon={<StickerIcon size={14} />}>
                      {/* 🔴 The level, not the id. A sticker whose level could not
                          be read keeps the id, which is what every sticker showed
                          before — the worst case is the old behaviour. */}
                      {sticker.level === null ? sticker.id : t.activityLevel(sticker.level)}
                    </StatusPill>
                  </span>
                ))}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
